import { create } from 'zustand';
import { registerUserScopedReset } from './session-read';
import api from '../services/api';
import { useAuthStore } from './auth.store';
import { useGuestStore } from './guest.store';
import type { DomainWithPaths, ProgressData } from '../types';

/**
 * The ProgressData a guest sees — the public course tree's totals plus the guest's
 * REAL local activity (quizzes taken in the intro, from guest.store localStorage).
 * Feeds the Progress page's Stats tab. Server-side nothing is
 * completed — these numbers are the device-local truth, which is exactly what the
 * "create an account to keep this" pitch is about.
 */
function guestProgressFrom(domains: DomainWithPaths[]): ProgressData {
  const totalLessons = domains.reduce((n, d) => n + (d.total_lessons ?? 0), 0);
  const totalPaths = domains.reduce((n, d) => n + d.paths.length, 0);
  const introLessons = domains[0]?.total_lessons ?? 0;

  const quizResults = Object.values(useGuestStore.getState().quizResults);
  const quizzesTaken = quizResults.length;

  return {
    completed_lessons: quizzesTaken,
    total_lessons: totalLessons,
    completed_domains: 0,
    total_domains: domains.length,
    completed_paths: 0,
    total_paths: totalPaths,
    completed_modules: 0,
    total_modules: domains.length,
    completed_units: 0,
    total_units: totalPaths,
    streak_days: 0,
    // The PROGRESS ring renders earned/total points; map the guest's local
    // completion onto the same lessons-based scale the summary line uses.
    earned_points: quizzesTaken,
    total_points: totalLessons,
    empathy_unlock: {
      unlocked: false,
      total: introLessons,
      completed: 0,
      percentage: 0,
      unlocked_at: null,
      next_lesson_id: null,
    },
  };
}

// ============================================
// Type Definitions
// ============================================

interface ProgressState {
  isLoading: boolean;
  progress: ProgressData | null;
  currentRequestId: string | null;
}

interface ProgressActions {
  setIsLoading: (isLoading: boolean) => void;
  setProgress: (progress: ProgressData | null) => void;
  fetchData: (silent?: boolean) => Promise<void>;
}

type ProgressStore = ProgressState & ProgressActions;

// ============================================
// Store Implementation
// ============================================

/**
 * The newest fetch in flight. A superseded fetch does not write its response,
 * so its caller must not resume until the request that will write has
 * settled: callers await fetchData() and then read the store (lesson-results
 * builds the course-complete card that way), and a superseded call that
 * returned at once handed them an empty store, which the card drew as
 * "0/0 Lessons, 0 Points" (walkthrough v2, 2026-10-24).
 */
let requestSeq = 0;
let newest: { id: string; request: Promise<void> } | null = null;

function followNewest(supersededId: string): Promise<void> {
  return newest && newest.id !== supersededId ? newest.request : Promise.resolve();
}

export const useProgressStore = create<ProgressStore>((set, get) => ({
  isLoading: false,
  progress: null,
  currentRequestId: null,

  setIsLoading: (isLoading: boolean) => set({ isLoading }),
  setProgress: (progress: ProgressData | null) => set({ progress }),

  fetchData: (silent = false) => {
    // A counter, not the clock: two calls in one millisecond must still be
    // told apart, or the older one's response is taken for the newer one's.
    const requestId = `progress-${++requestSeq}`;
    if (!silent) {
      set({ isLoading: true, currentRequestId: requestId });
    } else {
      set({ currentRequestId: requestId });
    }

    const request = (async (): Promise<void> => {
      try {
        // Guests have no server-side progress: derive the fresh-user shape from the
        // public course tree instead of calling the authed endpoint.
        if (useAuthStore.getState().isGuest) {
          const response = await api.get('/preview/domains');
          if (get().currentRequestId !== requestId) {
            return followNewest(requestId);
          }
          set({ isLoading: false, progress: guestProgressFrom(response.data.domains ?? []) });
          return;
        }

        const response = await api.get('/progress');

        // A newer request owns the store: this response is dropped, and the
        // caller waits for the newer one (see followNewest).
        if (get().currentRequestId !== requestId) {
          return followNewest(requestId);
        }

        const { data } = response.data;
        // Always reset isLoading on success — a silent request that superseded
        // a non-silent one must clear the loading state the non-silent one set.
        set({ isLoading: false, progress: data });
      } catch (error) {
        console.error('Failed to fetch progress:', error);
        if (get().currentRequestId !== requestId) {
          return followNewest(requestId);
        }
        // Only update error state if this request is still current
        if (!silent) {
          set({ isLoading: false });
        }
      }
    })();
    newest = { id: requestId, request };
    return request;
  },
}));

// Per-user: reset synchronously on every session change and on entering the
// guest tour (#940 r9). Clearing `currentRequestId` (and `newest`) also retires
// a read still in flight for the previous account: its answer fails the
// request-id check and writes nothing, so A's /progress cannot land in B's
// session (backlog c-029; lesson.store does the same).
registerUserScopedReset(() => {
  newest = null;
  useProgressStore.setState({ progress: null, isLoading: false, currentRequestId: null });
});
